/**
 * Product image zoom
 * Desktop (mouse):
 * - Clicking a gallery image (or its magnifying glass button) zooms in inside the
 *   image's own tile; the zoomed area follows the mouse. Click again or move the
 *   mouse out of the tile to zoom back out.
 * Touch / keyboard:
 * - Tapping a gallery image (or activating the button) opens it full screen in
 *   high resolution
 * - In the overlay, a tap/click zooms in on that exact spot; the zoomed image can
 *   be panned by scrolling/dragging, and another tap zooms back out
 * - The close button (and Escape) closes the overlay
 */
class ProductZoom {
  constructor(overlay) {
    this.overlay = overlay;
    this.stage = overlay.querySelector('.product-zoom__stage');
    this.image = overlay.querySelector('.product-zoom__image');
    this.closeButton = overlay.querySelector('.product-zoom__close');
    this.scale = 2.5;
    this.inlineScale = 2.5;
    this.isZoomed = false;
    this.lastTrigger = null;
    this.mouseQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    // Delegated, so every gallery image works - including ones added later
    document.addEventListener('click', (e) => {
      // Mobile floating magnifier: open whichever image is currently in view
      if (e.target.closest('.product__media--zoom-floating')) {
        e.preventDefault();
        const item = this.getVisibleItem();
        if (item) this.open(item);
        return;
      }

      const item = e.target.closest('.product__media--item-zoomable');
      if (!item || this.overlay.contains(item)) return;
      e.preventDefault();

      // e.detail is 0 for keyboard activation: keyboard users get the overlay
      if (this.mouseQuery.matches && e.detail > 0) {
        this.toggleInline(item, e);
      } else {
        this.open(item);
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.inlineItem) return;
      if (!this.inlineItem.contains(e.target)) {
        this.zoomOutInline();
        return;
      }
      this.setInlineOrigin(e);
    });

    // Start loading the sharp version as soon as the mouse is over an image, so
    // it's usually ready by the time the user clicks (no flash on zoom)
    document.addEventListener('mouseover', (e) => {
      const item = e.target.closest?.('.product__media--item-zoomable');
      if (item && this.mouseQuery.matches) this.preload(item.dataset.zoomSrc);
    });

    // Keep the zoomed tile clipped below the fixed header while scrolling
    document.addEventListener('scroll', () => this.clipBelowHeader(), { capture: true, passive: true });

    this.stage.addEventListener('click', (e) => {
      if (this.isZoomed) {
        this.zoomOut();
      } else if (this.image.complete && this.image.naturalWidth) {
        this.zoomIn(e.clientX, e.clientY);
      } else {
        // High-res image still loading: zoom on that spot as soon as it's ready
        this.cancelPendingZoom();
        const { clientX, clientY } = e;
        this.pendingZoom = () => {
          this.pendingZoom = null;
          this.zoomIn(clientX, clientY);
        };
        this.image.addEventListener('load', this.pendingZoom, { once: true });
      }
    });

    this.closeButton.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.hidden) this.close();
    });
  }

  // The zoomable slide closest to the middle of the gallery slider
  getVisibleItem() {
    const gallery = document.querySelector('.product__media');
    if (!gallery) return null;
    const galleryRect = gallery.getBoundingClientRect();
    const center = galleryRect.left + galleryRect.width / 2;
    let best = null;
    let bestDistance = Infinity;
    gallery.querySelectorAll('.product__media--item-zoomable').forEach((item) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = item;
      }
    });
    return best;
  }

  // Warm the browser cache with the high-res image (once per URL)
  preload(src) {
    this.preloaded = this.preloaded || new Map();
    if (!src || this.preloaded.has(src)) return this.preloaded?.get(src);
    const img = new Image();
    img.src = src;
    const ready = img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    this.preloaded.set(src, ready);
    return ready;
  }

  // Desktop: zoom inside the image's own tile, centered on the mouse
  toggleInline(item, e) {
    if (this.inlineItem === item) {
      this.zoomOutInline();
      return;
    }
    this.zoomOutInline();

    const img = item.querySelector('.product__media--image img');
    if (!img) return;

    // The already-loaded image zooms right away; a high-res layer on top of it
    // (same box, fit and transform) fades in once loaded - swapping the image's
    // own source mid-zoom is what caused the flash
    const layer = this.getHiResLayer(item, img);

    this.inlineItem = item;
    this.inlineImage = img;
    this.inlineLayer = layer;

    // From the magnifier button, zoom from the center; otherwise from the click
    if (e.target.closest('.product__media--zoom-button')) {
      this.setInlineOriginAt(50, 50);
    } else {
      this.setInlineOrigin(e);
    }

    item.classList.add('is-zoomed');
    this.clipBelowHeader();
    [img, layer].forEach((el) => { el.style.transform = `scale(${this.inlineScale})`; });

    const src = item.dataset.zoomSrc;
    Promise.resolve(this.preload(src)).then(() => {
      if (this.inlineLayer !== layer) return;
      if (layer.getAttribute('src') !== src) layer.src = src;
      const show = () => { if (this.inlineLayer === layer) layer.classList.add('is-loaded'); };
      layer.complete && layer.naturalWidth ? show() : layer.addEventListener('load', show, { once: true });
    });
  }

  getHiResLayer(item, img) {
    let layer = item.querySelector('.product__media--zoom-layer');
    if (!layer) {
      layer = document.createElement('img');
      layer.className = 'product__media--zoom-layer';
      layer.alt = '';
      layer.setAttribute('aria-hidden', 'true');
      item.appendChild(layer);
    }
    // Match the visible image exactly (measured unscaled, relative to the tile)
    const itemRect = item.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const style = getComputedStyle(img);
    Object.assign(layer.style, {
      left: `${imgRect.left - itemRect.left}px`,
      top: `${imgRect.top - itemRect.top}px`,
      width: `${imgRect.width}px`,
      height: `${imgRect.height}px`,
      objectFit: style.objectFit,
      objectPosition: style.objectPosition
    });
    return layer;
  }

  setInlineOrigin(e) {
    const rect = this.inlineItem.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1) * 100;
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1) * 100;
    this.setInlineOriginAt(x, y);
  }

  setInlineOriginAt(x, y) {
    [this.inlineImage, this.inlineLayer].forEach((el) => { el.style.transformOrigin = `${x}% ${y}%`; });
  }

  // The header is fixed and transparent; don't let a zoomed tile show through it
  clipBelowHeader() {
    if (!this.inlineItem) return;
    const header = document.querySelector('.header-desktop');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const hidden = Math.max(0, headerBottom - this.inlineItem.getBoundingClientRect().top);
    this.inlineItem.style.clipPath = hidden > 0 ? `inset(${hidden}px 0 0 0)` : '';
  }

  zoomOutInline() {
    if (!this.inlineItem) return;
    const { inlineItem: item, inlineImage: img, inlineLayer: layer } = this;
    item.classList.remove('is-zoomed');
    img.style.transform = '';
    layer.style.transform = '';
    layer.classList.remove('is-loaded');
    // Keep the header clip until the zoom-out transition has finished
    setTimeout(() => { if (this.inlineItem !== item) item.style.clipPath = ''; }, 300);
    this.inlineItem = null;
    this.inlineImage = null;
    this.inlineLayer = null;
  }

  open(item) {
    this.lastTrigger = item.querySelector('.product__media--zoom-button') || item;
    this.zoomOut();
    // Show the image that's already on the page right away, then swap in the
    // high-res version once it has loaded (no blank/flash on open)
    const pageImage = item.querySelector('.product__media--image img');
    const src = item.dataset.zoomSrc;
    this.image.src = pageImage?.currentSrc || src;
    this.openSrc = src;
    Promise.resolve(this.preload(src)).then(() => {
      if (this.openSrc === src && !this.overlay.hidden) this.image.src = src;
    });
    this.image.alt = item.dataset.zoomAlt || '';
    this.overlay.hidden = false;
    document.body.classList.add('overflow-hidden', 'product-zoom-open');
    requestAnimationFrame(() => this.overlay.classList.add('is-open'));
    this.closeButton.focus({ preventScroll: true });
  }

  close() {
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden', 'product-zoom-open');
    this.overlay.hidden = true;
    this.zoomOut();
    this.openSrc = null;
    this.image.removeAttribute('src');
    this.lastTrigger?.focus({ preventScroll: true });
  }

  // Where the image actually sits inside the stage while fitted (object-fit: contain)
  getFittedRect() {
    const stageRect = this.stage.getBoundingClientRect();
    const ratio = this.image.naturalWidth / this.image.naturalHeight;
    let width = stageRect.width;
    let height = width / ratio;
    if (height > stageRect.height) {
      height = stageRect.height;
      width = height * ratio;
    }
    return {
      left: stageRect.left + (stageRect.width - width) / 2,
      top: stageRect.top + (stageRect.height - height) / 2,
      width,
      height,
      stageRect
    };
  }

  zoomIn(clientX, clientY) {
    const fitted = this.getFittedRect();
    const { stageRect } = fitted;

    // Point that was tapped, as a fraction of the image
    const fx = Math.min(Math.max((clientX - fitted.left) / fitted.width, 0), 1);
    const fy = Math.min(Math.max((clientY - fitted.top) / fitted.height, 0), 1);

    // At least this.scale, and comfortably more than enough to cover the whole
    // screen, so there is room to keep the tapped spot under the finger on both
    // axes (e.g. landscape photos on a portrait phone)
    const coverScale = Math.max(stageRect.width / fitted.width, stageRect.height / fitted.height);
    const scale = Math.max(this.scale, coverScale * 1.5);
    const zoomedWidth = fitted.width * scale;
    const zoomedHeight = fitted.height * scale;

    this.image.style.width = `${zoomedWidth}px`;
    this.image.style.height = `${zoomedHeight}px`;
    this.overlay.classList.add('is-zoomed');
    this.isZoomed = true;

    // Scroll so the tapped spot stays under the finger/cursor
    const offsetX = Math.max((stageRect.width - zoomedWidth) / 2, 0);
    const offsetY = Math.max((stageRect.height - zoomedHeight) / 2, 0);
    this.stage.scrollLeft = offsetX + fx * zoomedWidth - (clientX - stageRect.left);
    this.stage.scrollTop = offsetY + fy * zoomedHeight - (clientY - stageRect.top);
  }

  cancelPendingZoom() {
    if (!this.pendingZoom) return;
    this.image.removeEventListener('load', this.pendingZoom);
    this.pendingZoom = null;
  }

  zoomOut() {
    this.cancelPendingZoom();
    this.image.style.width = '';
    this.image.style.height = '';
    this.overlay.classList.remove('is-zoomed');
    this.stage.scrollLeft = 0;
    this.stage.scrollTop = 0;
    this.isZoomed = false;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const overlay = document.querySelector('.product-zoom');
  if (overlay) new ProductZoom(overlay);
});
